import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { localizeNiuNiuLabel } from './NiuNiuRoundDetails';
import { useGameName } from '../utils/modeLabels';

// «Слабые места» на странице статистики: группы вопросов, где игрок ошибается чаще всего
// (по ответам рейтинговых прохождений за 60 дней, проверенным сервером)

export interface WeakSpot {
  gameId: string;
  kind: 'combination' | 'number' | 'multiplier' | 'picture' | 'hand' | 'betCombo' | 'abBet';
  value: string;
  attempts: number;
  errors: number;
}

// Подпись группы: «Номер 17», «×5», «Флеш-рояль» — общая для статистики игрока и карточки в админке
export function useWeakSpotLabel() {
  const { t } = useTranslation(['statistics', 'common', 'gameplay']);
  const { t: tGameplay } = useTranslation('gameplay');
  return (spot: WeakSpot) => {
    switch (spot.kind) {
      case 'number':
        return t('common:gameScreen.number', { n: spot.value });
      case 'multiplier':
        return t('statistics:weak.multiplier', { n: spot.value });
      case 'picture':
        return t(`statistics:weak.pictures.${spot.value}`, { defaultValue: spot.value });
      case 'hand':
        return localizeNiuNiuLabel(spot.value, tGameplay);
      case 'abBet':
        return spot.value.startsWith('side:')
          ? t('common:serverGames.andarBahar.side', { range: spot.value.slice(5).replace('-', '–') })
          : t(`common:serverGames.andarBahar.${spot.value}`);
      case 'betCombo': {
        const [bet, combo] = spot.value.split(':');
        return `${t(`common:serverGames.pokerBonus.bets.${bet}`)} · ${t(`common:serverGames.pokerBonus.combos.${combo}`)}`;
      }
      default:
        return t(`gameplay:paytable.combinations.${spot.value}`, { defaultValue: spot.value });
    }
  };
}

export default function WeakSpots({ spots }: { spots: WeakSpot[] }) {
  const { t } = useTranslation(['statistics', 'common', 'gameplay']);
  const label = useWeakSpotLabel();
  const gameName = useGameName();
  const navigate = useNavigate();
  if (spots.length === 0) return null;

  return (
    <section className="st-card st-wide" aria-label={t('statistics:weak.title')}>
      <h2 className="st-game">{t('statistics:weak.title')}</h2>
      <ul className="st-weak">
        {spots.map((spot) => (
          <li key={`${spot.gameId}:${spot.kind}:${spot.value}`}>
            <span className="st-weak-name">
              <b>{label(spot)}</b>
              <span>{gameName(spot.gameId)}</span>
            </span>
            <span className="st-weak-rate">
              <i style={{ width: `${Math.round((spot.errors / spot.attempts) * 100)}%` }} />
            </span>
            <span className="st-weak-count">{t('statistics:weak.errors', { errors: spot.errors, attempts: spot.attempts })}</span>
            <button type="button" className="st-weak-train" onClick={() => navigate(`/game/${spot.gameId}`)}>
              {t('statistics:weak.train')}
            </button>
          </li>
        ))}
      </ul>
      <p className="st-muted">{t('statistics:weak.hint')}</p>
    </section>
  );
}
